import type { CSSProperties } from "react";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import "./dot-spiral-loader.css";

const MATRIX_SIZE = 5;
const TOTAL_DOTS = MATRIX_SIZE * MATRIX_SIZE;

const SPIRAL_ORDER: readonly number[] = (() => {
  const order = new Array<number>(TOTAL_DOTS);
  let top = 0;
  let bottom = MATRIX_SIZE - 1;
  let left = 0;
  let right = MATRIX_SIZE - 1;
  let t = 0;
  while (top <= bottom && left <= right) {
    for (let c = left; c <= right; c += 1) order[top * MATRIX_SIZE + c] = t++;
    for (let r = top + 1; r <= bottom; r += 1) order[r * MATRIX_SIZE + right] = t++;
    if (top < bottom) {
      for (let c = right - 1; c >= left; c -= 1) order[bottom * MATRIX_SIZE + c] = t++;
    }
    if (left < right) {
      for (let r = bottom - 1; r > top; r -= 1) order[r * MATRIX_SIZE + left] = t++;
    }
    top += 1;
    bottom -= 1;
    left += 1;
    right -= 1;
  }
  return order;
})();

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
}

export interface DotSpiralLoaderProps {
  size?: number;
  dotSize?: number;
  speed?: number;
  animated?: boolean;
  className?: string;
  ariaLabel?: string;
}

export function DotSpiralLoader({
  size = 36,
  dotSize = 5,
  speed = 1.35,
  animated = true,
  className,
  ariaLabel = "Loading",
}: DotSpiralLoaderProps) {
  const reducedMotion = usePrefersReducedMotion();
  const isAnimated = animated && !reducedMotion;
  const gap = Math.max(1, Math.floor((size - dotSize * MATRIX_SIZE) / (MATRIX_SIZE - 1)));
  const speedScale = speed > 0 ? 1 / speed : 1;

  const rootStyle = {
    width: size,
    height: size,
    "--dsl-speed": speedScale,
  } as CSSProperties;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      className={cn("dot-spiral-loader", className)}
      style={rootStyle}
    >
      <div className="dot-spiral-loader-grid" style={{ gap }}>
        {SPIRAL_ORDER.map((order, index) => {
          const dotStyle = {
            width: dotSize,
            height: dotSize,
            "--dsl-order": order,
            ...(isAnimated ? null : { opacity: 0.16 + (order / (TOTAL_DOTS - 1)) * 0.78 }),
          } as CSSProperties;
          return (
            <span
              key={index}
              aria-hidden="true"
              className={cn("dot-spiral-loader-dot", isAnimated && "dot-spiral-loader-dot-animated")}
              style={dotStyle}
            />
          );
        })}
      </div>
    </div>
  );
}
